import { Modal } from 'antd'
import React, { useEffect, useState } from 'react'

const hideSpinnerStyles = `
  .number-input-no-spinner::-webkit-outer-spin-button,
  .number-input-no-spinner::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .number-input-no-spinner[type=number] {
    -moz-appearance: textfield;
  }
`

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style')
  styleSheet.textContent = hideSpinnerStyles
  document.head.appendChild(styleSheet)
}

interface PropertyFormData {
  propertyType: string
  unitSize: string
  bedrooms: string
  bathrooms: string
  floorNumber: string
  roomNumber: string
  roomPosition: string
  hasBalcony: boolean | null
  isPetFriendly: boolean | null
  maxGuests: string
  availability: string
  availableDate: any
  propertyPhotos: string[]
  videoTour: string | null
  floorPlan: string | null
  minRentalDuration: '1month' | '3-6months' | '1year' | null
  monthlyPrice: string
  sixMonthPrice: string
  yearlyPrice: string
  securityDeposit: string
  useHaupAgent: boolean
  agentFee: string
}

interface Step3Props {
  onNext: () => void
  onPrevious: () => void
  onStepProgress: (progress: number) => void
  formData: PropertyFormData
  updateFormData: (data: Partial<PropertyFormData>) => void
  isSubmittingDraft?: boolean
}

const Step3: React.FC<Step3Props> = ({
  onNext,
  onPrevious,
  onStepProgress,
  formData,
  updateFormData,
  isSubmittingDraft = false,
}) => {
  const [progress, setProgress] = useState(0)
  const [showHaupModal, setShowHaupModal] = useState(false)

  const calculateProgress = () => {
    const fields = []

    if (formData.minRentalDuration === '1month') {
      fields.push(formData.monthlyPrice, formData.sixMonthPrice, formData.yearlyPrice)
    } else if (formData.minRentalDuration === '3-6months') {
      fields.push(formData.sixMonthPrice, formData.yearlyPrice)
    } else if (formData.minRentalDuration === '1year') {
      fields.push(formData.yearlyPrice)
    }

    fields.push(formData.securityDeposit)

    const filledFields = fields.filter((field) => field && field !== '').length
    const progressPercentage =
      fields.length > 0 ? Math.round((filledFields / fields.length) * 100) : 0
    return progressPercentage
  }

  useEffect(() => {
    const newProgress = calculateProgress()
    setProgress(newProgress)
    onStepProgress(newProgress)
  }, [formData, onStepProgress])

  const handleInputChange = (field: string, value: any) => {
    updateFormData({ [field]: value })
  }

  const handleMinRentalDurationChange = (value: '1month' | '3-6months' | '1year') => {
    updateFormData({ minRentalDuration: value })
  }

  const handleToggleChange = (field: string, value: boolean) => {
    updateFormData({ [field]: value })
  }

  const isNextDisabled = progress < 100 || isSubmittingDraft

  return (
    <div
      style={{
        backgroundColor: '#fff',
        minHeight: '100vh',
        fontFamily: 'Poppins, sans-serif',
      }}
    >
      <div style={{ padding: '24px 16px' }}>
        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '400',
            }}
          >
            Minimum rental duration
          </label>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px',
              marginBottom: '8px',
            }}
          >
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #EDEFF4',
                cursor: 'pointer',
              }}
              onClick={() => handleMinRentalDurationChange('1month')}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor:
                      formData.minRentalDuration === '1month' ? '#4C4848' : 'transparent',
                    border: '2px solid #4C4848',
                    borderRadius: '50%',
                    marginRight: '8px',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {formData.minRentalDuration === '1month' && (
                    <div
                      style={{
                        width: '6px',
                        height: '6px',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                      }}
                    />
                  )}
                </div>
                <span style={{ fontSize: '14px', color: '#333' }}>1 month</span>
              </div>
            </div>

            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #EDEFF4',
                cursor: 'pointer',
              }}
              onClick={() => handleMinRentalDurationChange('3-6months')}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor:
                      formData.minRentalDuration === '3-6months' ? '#4C4848' : 'transparent',
                    border: '2px solid #4C4848',
                    borderRadius: '50%',
                    marginRight: '8px',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {formData.minRentalDuration === '3-6months' && (
                    <div
                      style={{
                        width: '6px',
                        height: '6px',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                      }}
                    />
                  )}
                </div>
                <span style={{ fontSize: '14px', color: '#333' }}>3-6 months</span>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #EDEFF4',
                cursor: 'pointer',
              }}
              onClick={() => handleMinRentalDurationChange('1year')}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor:
                      formData.minRentalDuration === '1year' ? '#4C4848' : 'transparent',
                    border: '2px solid #4C4848',
                    borderRadius: '50%',
                    marginRight: '8px',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {formData.minRentalDuration === '1year' && (
                    <div
                      style={{
                        width: '6px',
                        height: '6px',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                      }}
                    />
                  )}
                </div>
                <span style={{ fontSize: '14px', color: '#333' }}>1 year</span>
              </div>
            </div>
          </div>
        </div>

        {formData.minRentalDuration === '1month' && (
          <div style={{ marginBottom: '24px' }}>
            <h4
              style={{
                fontSize: '14px',
                color: '#333',
                marginBottom: '12px',
                fontWeight: '500',
                margin: '0 0 12px 0',
              }}
            >
              Monthly contract
            </h4>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                color: '#333',
                marginBottom: '8px',
                fontWeight: '400',
              }}
            >
              Price per month
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                placeholder="Enter price per month"
                value={formData.monthlyPrice}
                onChange={(e) => handleInputChange('monthlyPrice', e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                onKeyDown={(e) => {
                  if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                    e.preventDefault()
                  }
                }}
                className="number-input-no-spinner"
                style={{
                  width: '100%',
                  padding: '12px 40px 12px 12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Poppins, sans-serif',
                  boxSizing: 'border-box',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '14px',
                  color: '#666',
                }}
              >
                THB
              </span>
            </div>
          </div>
        )}

        {formData.minRentalDuration === '1month' && (
          <hr
            style={{ border: 'none', height: '1px', backgroundColor: '#EDEFF4', margin: '24px 0' }}
          />
        )}

        {(formData.minRentalDuration === '1month' ||
          formData.minRentalDuration === '3-6months') && (
          <div style={{ marginBottom: '24px' }}>
            <h4
              style={{
                fontSize: '14px',
                color: '#333',
                marginBottom: '12px',
                fontWeight: '500',
                margin: '0 0 12px 0',
              }}
            >
              3-6 months contract
            </h4>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                color: '#333',
                marginBottom: '8px',
                fontWeight: '400',
              }}
            >
              Price per month
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                placeholder="Enter price per month"
                value={formData.sixMonthPrice}
                onChange={(e) => handleInputChange('sixMonthPrice', e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                onKeyDown={(e) => {
                  if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                    e.preventDefault()
                  }
                }}
                className="number-input-no-spinner"
                style={{
                  width: '100%',
                  padding: '12px 40px 12px 12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Poppins, sans-serif',
                  boxSizing: 'border-box',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '14px',
                  color: '#666',
                }}
              >
                THB
              </span>
            </div>
          </div>
        )}

        {(formData.minRentalDuration === '1month' ||
          formData.minRentalDuration === '3-6months') && (
          <hr
            style={{ border: 'none', height: '1px', backgroundColor: '#EDEFF4', margin: '24px 0' }}
          />
        )}

        {(formData.minRentalDuration === '1month' ||
          formData.minRentalDuration === '3-6months' ||
          formData.minRentalDuration === '1year') && (
          <div style={{ marginBottom: '24px' }}>
            <h4
              style={{
                fontSize: '14px',
                color: '#333',
                marginBottom: '12px',
                fontWeight: '500',
                margin: '0 0 12px 0',
              }}
            >
              Yearly contract
            </h4>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                color: '#333',
                marginBottom: '8px',
                fontWeight: '400',
              }}
            >
              Price per month
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                placeholder="Enter price per month"
                value={formData.yearlyPrice}
                onChange={(e) => handleInputChange('yearlyPrice', e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                onKeyDown={(e) => {
                  if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                    e.preventDefault()
                  }
                }}
                className="number-input-no-spinner"
                style={{
                  width: '100%',
                  padding: '12px 40px 12px 12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Poppins, sans-serif',
                  boxSizing: 'border-box',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '14px',
                  color: '#666',
                }}
              >
                THB
              </span>
            </div>
          </div>
        )}

        {(formData.minRentalDuration === '1month' ||
          formData.minRentalDuration === '3-6months' ||
          formData.minRentalDuration === '1year') && (
          <hr
            style={{ border: 'none', height: '1px', backgroundColor: '#EDEFF4', margin: '24px 0' }}
          />
        )}

        <div style={{ marginBottom: '24px' }}>
          <h4
            style={{
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '500',
              margin: '0 0 12px 0',
            }}
          >
            Security deposit
          </h4>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
            <div
              style={{
                fontSize: '14px',
                color: '#666',
                minWidth: '140px',
              }}
            >
              Deposit amount
            </div>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                type="number"
                placeholder="Enter deposit amount"
                value={formData.securityDeposit}
                onChange={(e) => handleInputChange('securityDeposit', e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                onKeyDown={(e) => {
                  if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                    e.preventDefault()
                  }
                }}
                className="number-input-no-spinner"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Poppins, sans-serif',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <div
              style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: '#667085',
                marginTop: '2px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: 'bold',
                color: 'white',
              }}
            >
              !
            </div>
            <p
              style={{
                fontSize: '12px',
                color: '#667085',
                lineHeight: '1.4',
                margin: '0',
              }}
            >
              The standard security deposit in Thailand is 2 times the monthly rent.
            </p>
          </div>
        </div>

        <hr
          style={{ border: 'none', height: '1px', backgroundColor: '#EDEFF4', margin: '24px 0' }}
        />

        <div style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: '#333' }}>Use HAUP as agent</span>
              <div
                onClick={() => handleToggleChange('useHaupAgent', !formData.useHaupAgent)}
                style={{
                  width: '48px',
                  height: '24px',
                  backgroundColor: formData.useHaupAgent ? '#4C4848' : '#ccc',
                  borderRadius: '12px',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s',
                }}
              >
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: '2px',
                    left: formData.useHaupAgent ? '26px' : '2px',
                    transition: 'left 0.3s',
                  }}
                />
              </div>
            </div>
          </div>

          <div
            style={{
              backgroundColor: '#F4F5F9',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: '#667085',
                  marginTop: '2px',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  color: 'white',
                }}
              >
                !
              </div>
              <p
                style={{
                  fontSize: '12px',
                  color: '#667085',
                  lineHeight: '1.4',
                  margin: '0',
                }}
              >
                HAUP can handle your rental process, including renter screening and contract
                management. A fee of 50% of your first month's rent applies if you choose this
                service.
              </p>
            </div>
          </div>

          {formData.useHaupAgent && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div
                style={{
                  fontSize: '14px',
                  color: '#666',
                  minWidth: '140px',
                }}
              >
                Agent fee to HAUP
              </div>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  type="number"
                  placeholder="0.5 x one month rent"
                  value={formData.agentFee || ''}
                  disabled={true}
                  className="number-input-no-spinner"
                  style={{
                    width: '100%',
                    padding: '12px 40px 12px 12px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Poppins, sans-serif',
                    boxSizing: 'border-box',
                    backgroundColor: '#f5f5f5',
                    color: '#666',
                    cursor: 'not-allowed',
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '14px',
                    color: '#999',
                  }}
                >
                  THB
                </span>
              </div>
            </div>
          )}

          <div style={{ marginTop: '12px' }}>
            <button
              onClick={() => setShowHaupModal(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#4C4848',
                fontSize: '14px',
                cursor: 'pointer',
                textDecoration: 'none',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              Why choose HAUP as your agent?
              <span style={{ fontSize: '12px' }}>›</span>
            </button>
          </div>
        </div>

        <Modal
          open={showHaupModal}
          onCancel={() => setShowHaupModal(false)}
          footer={null}
          width={490}
          maskClosable={false}
          centered
          bodyStyle={{
            padding: '24px',
            minHeight: '550px',
          }}
        >
          <h2
            style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#333',
              marginBottom: '24px',
              marginTop: '16px',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Why choose HAUP as your rental agent
          </h2>

          <p
            style={{
              fontSize: '14px',
              color: '#666',
              lineHeight: '1.5',
              marginBottom: '24px',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            HAUP helps condo owners rent out their property quickly, safely, and hassle-free. By
            choosing us as your agent, you get:
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <img
                src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/31fxdhz6xasP22SsiwcdNXUMdB3WBbYGvySy9AB6pdZFUfkFBaGceoCpcxApyZ97VjP9qU3Lsx3Te4tkEsuwjPCw.png"
                alt="Verified renters"
                style={{ width: '24px', height: '24px', marginTop: '2px' }}
              />
              <div>
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#4C4848',
                    margin: '0 0 4px 0',
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  Verified renters
                </h3>
                <p
                  style={{
                    fontSize: '14px',
                    color: '#666',
                    margin: '0',
                    lineHeight: '1.4',
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  We screen all applicants to ensure reliability.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <img
                src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/4xiiTGmv91E1wwcjwpne1b9VuioqeNLQNfukSHSyt4voRtrXCceycpvC5VJTxcKFBjaptsG6F6YhFfgmjdU9JbaD.png"
                alt="Contract management"
                style={{ width: '24px', height: '24px', marginTop: '2px' }}
              />
              <div>
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#4C4848',
                    margin: '0 0 4px 0',
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  Contract management
                </h3>
                <p
                  style={{
                    fontSize: '14px',
                    color: '#666',
                    margin: '0',
                    lineHeight: '1.4',
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  We handle all paperwork and agreements.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <img
                src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/41i2kqz32id95q63yW7MdGUeVgD2qsBeDnJawQupYGim4zGmS5JfZxJDpMa7QE2AjvT356vWEmrHEguygPU8Gjk1.png"
                alt="Secure payments"
                style={{ width: '24px', height: '24px', marginTop: '2px' }}
              />
              <div>
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#4C4848',
                    margin: '0 0 4px 0',
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  Secure payments
                </h3>
                <p
                  style={{
                    fontSize: '14px',
                    color: '#666',
                    margin: '0',
                    lineHeight: '1.4',
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  Rent collection and deposit management handled professionally.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <img
                src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/3tqBBeYxn69QTZuSzY72Z4mHeNtbemPuEW99xu7qgKxm17BpAko72qjb9vUMiRe71YGccG6PrhhMYLn6bgMN4Hf7.png"
                alt="Expert support"
                style={{ width: '24px', height: '24px', marginTop: '2px' }}
              />
              <div>
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#4C4848',
                    margin: '0 0 4px 0',
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  Expert support
                </h3>
                <p
                  style={{
                    fontSize: '14px',
                    color: '#666',
                    margin: '0',
                    lineHeight: '1.4',
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  Our team guides you through the rental process from start to finish.
                </p>
              </div>
            </div>
          </div>
        </Modal>

        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginTop: '32px',
            paddingBottom: '32px',
          }}
        >
          <button
            onClick={onPrevious}
            style={{
              flex: '1',
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: '#666',
              border: '1px solid #e0e0e0',
              borderRadius: '24px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '16px',
              fontFamily: 'Poppins, sans-serif',
              transition: 'all 0.2s ease',
              transform: 'scale(1)',
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.95)'
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            Back
          </button>
          <button
            onClick={onNext}
            disabled={isNextDisabled}
            style={{
              flex: '1',
              padding: '12px 24px',
              backgroundColor: isNextDisabled ? '#ccc' : '#4C4848',
              color: 'white',
              border: 'none',
              borderRadius: '24px',
              cursor: isNextDisabled ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              fontSize: '16px',
              fontFamily: 'Poppins, sans-serif',
              opacity: isNextDisabled ? 0.6 : 1,
              transition: 'all 0.2s ease',
              transform: 'scale(1)',
              boxShadow: !isNextDisabled ? '0 2px 8px rgba(183, 135, 90, 0.3)' : 'none',
            }}
            onMouseDown={(e) => {
              if (!isNextDisabled) {
                e.currentTarget.style.transform = 'scale(0.95)'
              }
            }}
            onMouseUp={(e) => {
              if (!isNextDisabled) {
                e.currentTarget.style.transform = 'scale(1)'
              }
            }}
            onMouseLeave={(e) => {
              if (!isNextDisabled) {
                e.currentTarget.style.transform = 'scale(1)'
              }
            }}
          >
            {isSubmittingDraft ? 'Saving...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Step3
